import { MODEL_TYPES, RENDER_STATUS, THREE_D_FILE_DOWNLOADS } from '../constants/constants';
import {
  apiArchivePropsGroup,
  apiGetCollect3DFilesRequestAPI,
  apiModelAdminDelete,
  apiModelDeleteAndRestore,
  apiPatchCollect3DFilesRequestAPI,
  apiPostCollect3DFilesRequestAPI,
} from '../helpers/api';
import { errorToast, successToast, warningToast } from '../helpers/toastHelper';
import { useDispatch, useSelector } from 'react-redux';

import { addToCollectionActions } from '../redux/slicers/addToCollectionPopupSlicer';
import { isAdminOrOwner } from '../helpers/userHelper';
import {
  addOneMonthAddCurrentYear,
  clearCacheFromAPI,
  objectToFormData,
  objectToSearchParams,
} from '../helpers/jsHelper';
import { sharePopupActions } from '../redux/slicers/sharePopupSlicer';
import { useInfoDialogaActions } from './useInfoDialogaActions';
import { useUserContext } from '../contexts/UserContext';
import { WarningContentInfoIcon } from '../assets/icons';
import PaymentDoneIcon from '../components/icons/PaymentDone';
import { getAndSetCookieData } from '../helpers/apiHelper';
import { useDownloadEntity } from './useDownloadEntity';

export const useModelActions = ({
  selectors,
  updateApi,
  item,
  deleteConfig,
  modelType,
  deleteSuccessMessage,
  refetchList,
}) => {
  const dispatch = useDispatch();
  const { currentUser, isGuestUser } = useUserContext();
  const { openInfoDialog, closeInfoDialog } = useInfoDialogaActions();
  const { download3DFilesZip } = useDownloadEntity();
  const favouriteLoading = useSelector((state) => {
    if (!item?.id) return null;
    return selectors.selectIsFavouriteLoading(state, item.id);
  });

  const onFavouriteToggle = (clickedItem) => async (event) => {
    const tempItem = clickedItem || item;
    if (!tempItem?.id) return;

    const isEventChecked = typeof event?.target?.checked === 'boolean';
    const nextFavourite = isEventChecked ? event.target.checked : !tempItem.is_favourite;

    if (nextFavourite === tempItem.is_favourite) return;

    const response = await dispatch(
      updateApi({
        id: tempItem.id,
        is_favourite: nextFavourite,
      })
    );

    const updatedFavourite = response?.is_favourite;
    if (typeof updatedFavourite === 'boolean' && updatedFavourite === nextFavourite) {
      localStorage.setItem('CACHE_TIMESTAMP', Date.now());
      return;
    }

    if (typeof updatedFavourite !== 'boolean') {
      localStorage.setItem('CACHE_TIMESTAMP', Date.now());
    }
  };

  const onDelete = (id) => {
    const { member_details, owner_detail } = item;
    const member_id =
      modelType === MODEL_TYPES.COLLECTION ? owner_detail.user.member_id : member_details.member_id;

    if (!isAdminOrOwner(currentUser) && member_id !== currentUser.id) {
      warningToast('You do not have ownership to delete this item.', {
        toastId: `CAN_NOT_DELETE_${item.id}`,
      });
      return;
    }

    openInfoDialog({
      title: deleteConfig.title,
      description: deleteConfig.description || 'Your items can be restored from the Trash area.',
      cancelButtonText: deleteConfig?.cancelButtonText || 'No',
      buttonText: deleteConfig?.buttonText || 'Yes',
      onButtonClick: async () => {
        const res = await dispatch(
          deleteConfig.deleteApi({
            id,
            newSuccessMessage: deleteSuccessMessage,
            query: deleteConfig.query,
          })
        );
        window.$refetch = true;
      },
      okBtnColor: deleteConfig?.okBtnColor,
      cancelBtnColor: deleteConfig?.cancelBtnColor,
      okBtnClassName: deleteConfig?.okBtnClassName,
    });
  };

  const onArchive = (id) => {
    if (!id) return;
    openInfoDialog({
      title: 'Are you sure you want to archive this products group?',
      description: 'You can view the archived products group through filter panel.',
      cancelButtonText: 'Cancel',
      buttonText: 'Archive',
      onButtonClick: async () => {
        const payload = {
          is_archived: true,
        };
        clearCacheFromAPI();
        const res = await apiArchivePropsGroup(id, payload);
        if (res?.status) {
          successToast(res?.message || 'Products group archived successfully.');
          refetchList && refetchList?.();
        }
      },
      okBtnColor: deleteConfig?.okBtnColor,
      cancelBtnColor: deleteConfig?.cancelBtnColor,
      okBtnClassName: deleteConfig?.okBtnClassName,
    });
  };

  const onRestore = async (id) => {
    if (!id) return;
    const payload = {
      is_archived: false,
    };
    clearCacheFromAPI();
    const res = await apiArchivePropsGroup(id, payload);
    if (res?.status) {
      successToast(res?.message || 'Products group restored successfully.');
      refetchList && refetchList?.();
    }
  };

  const handleSuccessMessage = (date) => {
    openInfoDialog({
      topIcon: <PaymentDoneIcon />,
      title: 'Your delete request has been submitted.',
      description: (
        <>
          {date ? (
            <>
              {`All the items (projects, props, renders, etc.) using this model will no longer be accessible after`}{' '}
              <strong>
                <b>{date}</b>
              </strong>
              .
            </>
          ) : (
            <>
              All items (projects, props, renders, etc.) using these model will no longer be
              accessible after their respective deletion dates.
            </>
          )}
        </>
      ),
      titleStyle: { textAlign: 'start' },
      buttonText: 'Okay',
      size: 'lg',
      onButtonClick: () => closeInfoDialog(),
    });
  };

  const onSoftDelete = (id) => {
    if (!id) return;
    // const date = item?.delete_expired_date && addOneMonthAddCurrentYear(item?.delete_expired_date);
    const date = null;

    openInfoDialog({
      topIcon: <WarningContentInfoIcon />,
      title: 'Are you sure you want to delete this model?',
      description: (
        <>
          {!isGuestUser() && item?.is_customer_product_expired ? (
            <p>
              {`You’ve selected inactive model. Once confirmed, these model will be deleted
            permanently. This action cannot be undone.`}
            </p>
          ) : (
            <>
              {date ? (
                <>
                  {`This model maybe linked to certain items (projects, props, renders, etc.). You won’t be able to access these items after`}{' '}
                  <strong>
                    <b>{date}</b>
                  </strong>
                  .
                </>
              ) : (
                <>
                  {!isGuestUser() ? (
                    <p>
                      The selected model may be linked to certain items(projects, props, renders,
                      etc). After the respective subscription period ends, you will no longer have
                      access to these linked items.
                    </p>
                  ) : (
                    <p>
                      The selected model may be linked to certain items(projects, props, renders,
                      etc)
                    </p>
                  )}
                  {!isGuestUser() && (
                    <p>
                      To retain access, you can restore this model before the respective
                      subscription period ends.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </>
      ),
      secondLineDescription:
        item?.is_customer_product_expired || !date || isGuestUser()
          ? ''
          : `You can restore the model before this date to retain access.`,
      cancelButtonText: 'Cancel',
      buttonText: 'Confirm',
      size: 'lg',
      titleStyle: { textAlign: 'start' },
      onButtonClick: async () => {
        clearCacheFromAPI();
        
        if (isGuestUser()) {
          const formData = new FormData();
          formData.append('id', id);
          const res = await apiModelAdminDelete(formData);
          if (res?.status) {
            refetchList && refetchList?.();
            successToast(res?.message || 'Your model deleted successfully');
          } else {
            errorToast(res?.message || 'Something went wrong. Please try again.');
          }
          return;
        }
        const formData = new FormData();
        formData.append('is_delete_on_expire', true);
        formData.append('product_ids', id);
        const res = await apiModelDeleteAndRestore(formData);
        if (res?.status) {
          handleSuccessMessage(date);
          refetchList && refetchList?.();
        }
      },
      okBtnColor: deleteConfig?.okBtnColor,
      cancelBtnColor: deleteConfig?.cancelBtnColor,
      okBtnClassName: deleteConfig?.okBtnClassName,
    });
  };

  const onSoftDeleteRestore = async (id) => {
    if (!id) return;
    openInfoDialog({
      topIcon: <WarningContentInfoIcon />,
      title: 'Are you sure you want to restore this model?',
      description: `This model maybe linked to certain items (projects, props, renders etc.). Restoring it will allow you to use these items without any restrictions.`,
      cancelButtonText: 'Cancel',
      buttonText: 'Confirm',
      size: 'lg',
      titleStyle: { textAlign: 'start' },
      onButtonClick: async () => {
        clearCacheFromAPI();

        const formData = new FormData();
        formData.append('is_delete_on_expire', false);
        formData.append('product_ids', id);
        const res = await apiModelDeleteAndRestore(formData, 'Your model restored successfully');
        if (res?.status) {
          refetchList && refetchList?.();
        }
      },
      okBtnColor: deleteConfig?.okBtnColor,
      cancelBtnColor: deleteConfig?.cancelBtnColor,
      okBtnClassName: deleteConfig?.okBtnClassName,
    });
  };

  const onShare = () => {
    dispatch(
      sharePopupActions.share({
        [modelType]: [item],
      })
    );
  };

  const addToCollection = () => {
    dispatch(
      addToCollectionActions.add({
        items: [item],
        modelType: modelType,
      })
    );
  };

  const handleUpdateStatusToProcess = async (res, zip_file_details, objectIdData, modalTypeId) => {
    let cookieExpiryTime = 1800000; // 30 min
    if (MODEL_TYPES?.PRODUCT === modalTypeId) {
      cookieExpiryTime = 300000; // 5 min
    }
    const payload = {
      status: RENDER_STATUS.STATUS_PROCESSING,
    };
    const formData = objectToFormData(payload);
    res = await apiPatchCollect3DFilesRequestAPI({
      id: zip_file_details?.id,
      formData,
    });
    if (!res?.data) {
      res = { status: true, data: res };
      getAndSetCookieData({
        cookieName: `${modalTypeId}_${objectIdData}`,
        cookieData: res?.data || null,
        cookieExpireTime: cookieExpiryTime,
      });
    }
    return res;
  };

  async function requestTo3DFilesCollection({ data, rest, onUpdate = null }) {
    let res = {};
    const { is_prop_group, is_v1, is_old_obj } = data || {};
    let { zip_file_details } = data || {};

    const objectIdData =
      rest?.itemModelType === MODEL_TYPES.PROJECTRENDER ? data?.main_render : data?.id;
    const modalTypeId = THREE_D_FILE_DOWNLOADS[rest?.itemModelType];
    let cookieExpiryTime = 1800000; // 30 min
    if (MODEL_TYPES?.PRODUCT === rest?.itemModelType) {
      cookieExpiryTime = 300000; // 5 min
    }
    const qData = getAndSetCookieData({
      cookieName: `${modalTypeId}_${objectIdData}`,
    });
    if (qData) {
      zip_file_details = qData;
    }
    if (!data) return;

    if (is_prop_group || is_v1 || is_old_obj) {
      warningToast(
        'You can not request for this files. You need to do 1 render and than request from render list.'
      );
      return;
    }

    if (zip_file_details?.status === RENDER_STATUS.STATUS_FAILED) {
      res = await handleUpdateStatusToProcess(res, zip_file_details, objectIdData, modalTypeId);
    } else {
      const payload = {
        obj_id: objectIdData,
        model_type: modalTypeId,
      };
      const _params = objectToSearchParams({
        object_id: payload.obj_id,
        model_type: payload.model_type,
      });
      const isRequestCollected = await apiGetCollect3DFilesRequestAPI(_params);
      if (!isRequestCollected?.results?.length) {
        const formData = objectToFormData(payload);
        res = await apiPostCollect3DFilesRequestAPI({
          formData,
        });
        getAndSetCookieData({
          cookieName: `${payload?.model_type}_${payload?.obj_id}`,
          cookieData: res?.data || null,
          cookieExpireTime: cookieExpiryTime,
        });
      } else {
        const zipData = isRequestCollected?.results?.[0] || qData;
        getAndSetCookieData({
          cookieName: `${modalTypeId}_${objectIdData}`,
          cookieData: zipData || null,
          cookieExpireTime: cookieExpiryTime,
        });
        onUpdate?.({
          ...data,
          zip_file_details: zipData,
        });
        if (
          !qData &&
          zipData?.status !== RENDER_STATUS.STATUS_DONE &&
          zipData?.status !== RENDER_STATUS.STATUS_FAILED &&
          !zipData?.zip_file
        ) {
          successToast(
            'Your request has been successfully created. Please check after a few minutes'
          );
        }

        if (zipData) {
          if (zipData && zipData?.status === RENDER_STATUS.STATUS_DONE && zipData?.zip_file) {
            download3DFilesZip({
              ...data,
              zip_file_details: zipData,
            });
          }
          if (zipData?.status === RENDER_STATUS.STATUS_FAILED) {
            res = await handleUpdateStatusToProcess(res, zipData, objectIdData, modalTypeId);
          }
        }
      }
    }
    if (res?.status && res?.data) {
      successToast('Your request has been successfully created. Please check after a few minutes');
      onUpdate?.({
        ...data,
        zip_file_details: res?.data,
      });
    }
  }

  return {
    favouriteLoading,
    onFavouriteToggle,
    onDelete,
    onShare,
    addToCollection,
    requestTo3DFilesCollection,
    onRestore,
    onArchive,
    onSoftDelete,
    onSoftDeleteRestore,
  };
};
